/**
 * AgentForm Component
 *
 * Form for creating new agent slots with:
 * - Name input (slug format)
 * - Host selection
 * - Working directory path
 * - Optional display name, description, avatar
 *
 * @module components/admin/AgentForm
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Bot,
  Server,
  FolderOpen,
  FileText,
  Image,
  Save,
  X,
  Loader2,
  AlertCircle,
  History,
  Trash2,
  Check,
  StickyNote,
  User,
  Key,
  Terminal,
  ChevronDown,
  ChevronRight,
  ScrollText,
  Sparkles,
  Shield,
  Cpu
} from 'lucide-react';
import { clearAgentHistory, getAgentHistoryStats } from '../session/SessionChatInput.jsx';
import AvatarUpload from './AvatarUpload.jsx';

// ============================================
// Validation
// ============================================

const VALIDATION = {
  NAME: {
    MIN: 1,
    MAX: 64,
    PATTERN: /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/
  },
  PATH: {
    PATTERN: /^\//
  }
};

function validateName(value) {
  if (!value || value.trim().length === 0) return 'Name is required';
  if (value.length > VALIDATION.NAME.MAX) return `Max ${VALIDATION.NAME.MAX} characters`;
  if (!VALIDATION.NAME.PATTERN.test(value)) return 'Lowercase alphanumeric with hyphens only';
  return null;
}

function validateHostId(value) {
  if (!value) return 'Host is required';
  return null;
}

function validateWorkingDirectory(value) {
  if (!value || value.trim().length === 0) return 'Working directory is required';
  if (!VALIDATION.PATH.PATTERN.test(value)) return 'Must be absolute path (start with /)';
  return null;
}

// ============================================
// Components
// ============================================

function FormField({ id, label, required, error, hint, icon: Icon, children }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium text-gray-200">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        {label}
        {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {error ? (
        <p className="flex items-center gap-1 text-xs text-red-400">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
}

// ============================================
// Input History Section Component
// ============================================

function InputHistorySection({ agentId }) {
  const [stats, setStats] = useState({ count: 0, lastAccess: null });
  const [isClearing, setIsClearing] = useState(false);
  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    if (agentId) {
      setStats(getAgentHistoryStats(agentId));
    }
  }, [agentId]);

  const handleClear = useCallback(() => {
    if (!agentId) return;
    setIsClearing(true);
    try {
      clearAgentHistory(agentId);
      setStats({ count: 0, lastAccess: null });
      setCleared(true);
      setTimeout(() => setCleared(false), 3000);
    } finally {
      setIsClearing(false);
    }
  }, [agentId]);

  const formatLastAccess = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-200">
        <History className="w-4 h-4 text-gray-400" />
        Input History
      </label>
      <div className="flex items-center justify-between p-3 rounded-md bg-gray-900 border border-gray-700">
        <div className="space-y-1">
          <div className="text-sm text-gray-300">
            {stats.count === 0 ? 'No saved history' : `${stats.count} saved ${stats.count === 1 ? 'entry' : 'entries'}`}
          </div>
          {stats.lastAccess && (
            <div className="text-xs text-gray-500">Last used: {formatLastAccess(stats.lastAccess)}</div>
          )}
        </div>
        {stats.count > 0 && (
          <button
            type="button"
            onClick={handleClear}
            disabled={isClearing || cleared}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
              cleared
                ? 'bg-green-500/20 text-green-400'
                : 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30'
            } disabled:opacity-50`}
          >
            {isClearing ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Clearing...</>
            ) : cleared ? (
              <><Check className="w-3.5 h-3.5" /> Cleared</>
            ) : (
              <><Trash2 className="w-3.5 h-3.5" /> Clear History</>
            )}
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Arrow-up/down navigation in chat input. Auto-deleted after 30 days of inactivity.
      </p>
    </div>
  );
}

// ============================================
// Self-Note Settings Section Component
// ============================================

function SelfNoteSettingsSection({ agent, onUpdate }) {
  const [selfNoteEnabled, setSelfNoteEnabled] = useState(agent?.selfNoteEnabled ?? false);
  const [selfNoteThreshold, setSelfNoteThreshold] = useState(agent?.selfNoteThreshold ?? 74);
  const [selfNoteMaxLength, setSelfNoteMaxLength] = useState(agent?.selfNoteMaxLength ?? 2000);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Sync with agent prop
  useEffect(() => {
    if (agent?.selfNoteEnabled !== undefined) setSelfNoteEnabled(agent.selfNoteEnabled);
    if (agent?.selfNoteThreshold !== undefined) setSelfNoteThreshold(agent.selfNoteThreshold);
    if (agent?.selfNoteMaxLength !== undefined) setSelfNoteMaxLength(agent.selfNoteMaxLength);
  }, [agent?.selfNoteEnabled, agent?.selfNoteThreshold, agent?.selfNoteMaxLength]);

  const handleSave = useCallback(async () => {
    if (!agent?.id) return;
    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(`/api/agents/${agent.id}/self-note-settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          selfNoteEnabled,
          selfNoteThreshold,
          selfNoteMaxLength,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error?.message || 'Failed to save settings');
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [agent?.id, selfNoteEnabled, selfNoteThreshold, selfNoteMaxLength, onUpdate]);

  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-200">
        <StickyNote className="w-4 h-4 text-gray-400" />
        Self-Note Settings
      </label>
      <div className="p-4 rounded-md bg-gray-900 border border-gray-700 space-y-4">
        {/* Enable Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-300">Enable Self-Notes</div>
            <div className="text-xs text-gray-500">Allow agent to save notes before context compaction</div>
          </div>
          <button
            type="button"
            onClick={() => setSelfNoteEnabled(!selfNoteEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              selfNoteEnabled ? 'bg-amber-500' : 'bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                selfNoteEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Threshold Slider */}
        <div className={selfNoteEnabled ? '' : 'opacity-50 pointer-events-none'}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-300">Context Threshold</div>
            <div className="text-sm text-amber-400 font-medium">{selfNoteThreshold}%</div>
          </div>
          <input
            type="range"
            min="50"
            max="90"
            value={selfNoteThreshold}
            onChange={(e) => setSelfNoteThreshold(parseInt(e.target.value, 10))}
            disabled={!selfNoteEnabled}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>50%</span>
            <span>Triggers self-note prompt when context usage reaches threshold</span>
            <span>90%</span>
          </div>
        </div>

        {/* Max Length Input */}
        <div className={selfNoteEnabled ? '' : 'opacity-50 pointer-events-none'}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-300">Max Note Length</div>
            <div className="text-sm text-gray-400">{selfNoteMaxLength} chars</div>
          </div>
          <input
            type="number"
            min="500"
            max="10000"
            step="100"
            value={selfNoteMaxLength}
            onChange={(e) => setSelfNoteMaxLength(Math.max(500, Math.min(10000, parseInt(e.target.value, 10) || 2000)))}
            disabled={!selfNoteEnabled}
            className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-600 text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
          />
          <div className="text-xs text-gray-500 mt-1">Maximum characters for each self-note (500-10000)</div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || saved}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
              saved
                ? 'bg-green-500/20 text-green-400'
                : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30'
            } disabled:opacity-50`}
          >
            {isSaving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</>
            ) : saved ? (
              <><Check className="w-3.5 h-3.5" /> Saved</>
            ) : (
              <><Save className="w-3.5 h-3.5" /> Save Settings</>
            )}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        Self-notes are automatically saved before context compaction to preserve important information.
      </p>
    </div>
  );
}

// ============================================
// System Prompt Section Component
// ============================================

function SystemPromptSection({ agent, onUpdate }) {
  const [systemPrompts, setSystemPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [systemPromptEnabled, setSystemPromptEnabled] = useState(agent?.systemPromptEnabled ?? false);
  const [systemPromptId, setSystemPromptId] = useState(agent?.systemPromptId || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Load available system prompts
  useEffect(() => {
    async function loadPrompts() {
      try {
        const res = await fetch('/api/system-prompts', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setSystemPrompts(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load system prompts:', err);
      } finally {
        setIsLoading(false);
      }
    }
    loadPrompts();
  }, []);

  // Sync with agent prop
  useEffect(() => {
    if (agent?.systemPromptEnabled !== undefined) setSystemPromptEnabled(agent.systemPromptEnabled);
    if (agent?.systemPromptId !== undefined) setSystemPromptId(agent.systemPromptId || '');
  }, [agent?.systemPromptEnabled, agent?.systemPromptId]);

  const handleSave = useCallback(async () => {
    if (!agent?.id) return;
    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(`/api/agents/${agent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          systemPromptEnabled,
          systemPromptId: systemPromptEnabled && systemPromptId ? systemPromptId : null,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error?.message || data.message || 'Failed to save settings');
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [agent?.id, systemPromptEnabled, systemPromptId, onUpdate]);

  // Don't render if no system prompts exist (per PRD requirement)
  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-200">
          <ScrollText className="w-4 h-4 text-gray-400" />
          System Prompt
        </label>
        <div className="flex items-center gap-2 p-4 rounded-md bg-gray-900 border border-gray-700">
          <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  // Hide section if no system prompts exist
  if (systemPrompts.length === 0) {
    return null;
  }

  const selectedPrompt = systemPrompts.find(p => p.id === systemPromptId);

  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-200">
        <ScrollText className="w-4 h-4 text-gray-400" />
        System Prompt Extension
      </label>
      <div className="p-4 rounded-md bg-gray-900 border border-gray-700 space-y-4">
        {/* Enable Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-300">Enable System Prompt</div>
            <div className="text-xs text-gray-500">Extend agent persona with custom system prompt</div>
          </div>
          <button
            type="button"
            onClick={() => setSystemPromptEnabled(!systemPromptEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              systemPromptEnabled ? 'bg-amber-500' : 'bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                systemPromptEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Prompt Dropdown */}
        <div className={systemPromptEnabled ? '' : 'opacity-50 pointer-events-none'}>
          <div className="text-sm text-gray-300 mb-2">Select Prompt</div>
          <select
            value={systemPromptId}
            onChange={(e) => setSystemPromptId(e.target.value)}
            disabled={!systemPromptEnabled}
            className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-600 text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
          >
            <option value="">Select a system prompt...</option>
            {systemPrompts.map(prompt => (
              <option key={prompt.id} value={prompt.id}>
                {prompt.name}
              </option>
            ))}
          </select>
          {selectedPrompt && (
            <div className="mt-2 text-xs text-gray-500">
              This prompt will be loaded on agent spawn via --append-system-prompt
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || saved}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
              saved
                ? 'bg-green-500/20 text-green-400'
                : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30'
            } disabled:opacity-50`}
          >
            {isSaving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</>
            ) : saved ? (
              <><Check className="w-3.5 h-3.5" /> Saved</>
            ) : (
              <><Save className="w-3.5 h-3.5" /> Save Settings</>
            )}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        System prompts help maintain agent persona consistency. Changes take effect on next spawn.
      </p>
    </div>
  );
}

// ============================================
// Whisper Mode Section Component
// ============================================

const TRUST_MODE_OPTIONS = {
  confirm: {
    label: 'Confirm',
    description: 'Lead-Agent kann Änderungen vorschlagen. Sie müssen im Template-Manager bestätigt werden.',
  },
  full_trust: {
    label: 'Full Trust',
    description: 'Lead-Agent kann Änderungen direkt durchführen. Alle Änderungen sind über die Version-History rückgängig machbar.',
  },
};

function EditTrustModeSection({ agent }) {
  const [trustMode, setTrustMode] = useState(agent?.editTrustMode || 'confirm');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (agent?.editTrustMode) setTrustMode(agent.editTrustMode);
  }, [agent?.editTrustMode]);

  const handleSave = useCallback(async () => {
    if (!agent?.id) return;
    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(`/api/agents/${agent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ editTrustMode: trustMode }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error?.message || data.message || 'Failed to save settings');
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [agent?.id, trustMode]);

  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-200">
        <Shield className="w-4 h-4 text-gray-400" />
        CLAUDE.md Edit Trust Mode
      </label>
      <div className="p-4 rounded-md bg-gray-900 border border-gray-700 space-y-4">
        <div className="space-y-2">
          {Object.entries(TRUST_MODE_OPTIONS).map(([value, option]) => (
            <label
              key={value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                trustMode === value
                  ? 'bg-indigo-600/15 border-indigo-500/40'
                  : 'bg-gray-800/50 border-gray-700/50 hover:border-gray-600'
              }`}
            >
              <input
                type="radio"
                name="editTrustMode"
                value={value}
                checked={trustMode === value}
                onChange={() => setTrustMode(value)}
                className="mt-0.5 accent-indigo-500"
              />
              <div>
                <div className="text-sm font-medium text-gray-200">{option.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{option.description}</div>
              </div>
            </label>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || saved || trustMode === (agent?.editTrustMode || 'confirm')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
              saved
                ? 'bg-green-500/20 text-green-400'
                : 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 border border-indigo-500/30'
            } disabled:opacity-50`}
          >
            {isSaving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</>
            ) : saved ? (
              <><Check className="w-3.5 h-3.5" /> Saved</>
            ) : (
              <><Save className="w-3.5 h-3.5" /> Save Settings</>
            )}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        Controls how the lead agent's CLAUDE.md edits are handled for this agent.
      </p>
    </div>
  );
}

function WhisperModeSection({ agent, onUpdate }) {
  const [whisperEnabled, setWhisperEnabled] = useState(agent?.whisperEnabled ?? true);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Sync with agent prop
  useEffect(() => {
    if (agent?.whisperEnabled !== undefined) setWhisperEnabled(agent.whisperEnabled);
  }, [agent?.whisperEnabled]);

  const handleSave = useCallback(async () => {
    if (!agent?.id) return;
    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(`/api/agents/${agent.id}/whisper-mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: whisperEnabled }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error?.message || data.message || 'Failed to save settings');
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [agent?.id, whisperEnabled, onUpdate]);

  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-200">
        <Sparkles className="w-4 h-4 text-gray-400" />
        Whisper Mode
      </label>
      <div className="p-4 rounded-md bg-gray-900 border border-gray-700 space-y-4">
        {/* Enable Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-300">Enable Whisper Mode</div>
            <div className="text-xs text-gray-500">Send automatic system reminders to agent</div>
          </div>
          <button
            type="button"
            onClick={() => setWhisperEnabled(!whisperEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              whisperEnabled ? 'bg-amber-500' : 'bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                whisperEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || saved}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
              saved
                ? 'bg-green-500/20 text-green-400'
                : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30'
            } disabled:opacity-50`}
          >
            {isSaving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</>
            ) : saved ? (
              <><Check className="w-3.5 h-3.5" /> Saved</>
            ) : (
              <><Save className="w-3.5 h-3.5" /> Save Settings</>
            )}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        Whisper mode sends context-based reminders (e.g., "Document insights", "Manage tasks").
      </p>
    </div>
  );
}

const MODEL_OPTIONS = [
  { value: '', label: 'Default (CLI)', description: 'Use Claude CLI default model' },
  { value: 'opus', label: 'Opus', color: 'text-purple-400', description: 'Most capable, complex tasks' },
  { value: 'sonnet', label: 'Sonnet', color: 'text-blue-400', description: 'Balanced speed and quality' },
  { value: 'haiku', label: 'Haiku', color: 'text-green-400', description: 'Fast, lightweight tasks' },
];

function ModelSection({ agent, onUpdate }) {
  const [model, setModel] = useState(agent?.model || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (agent?.model !== undefined) setModel(agent.model || '');
  }, [agent?.model]);

  const handleSave = useCallback(async () => {
    if (!agent?.id) return;
    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(`/api/agents/${agent.id}/model`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ model: model || null }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error?.message || data.message || 'Failed to save model');
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [agent?.id, model, onUpdate]);

  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-200">
        <Cpu className="w-4 h-4 text-gray-400" />
        Default Model
      </label>
      <div className="p-4 rounded-md bg-gray-900 border border-gray-700 space-y-4">
        <div className="flex items-center gap-3">
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-sm text-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          >
            {MODEL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} — {opt.description}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || saved}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
              saved
                ? 'bg-green-500/20 text-green-400'
                : 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 border border-indigo-500/30'
            } disabled:opacity-50`}
          >
            {isSaving ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</>
            ) : saved ? (
              <><Check className="w-3.5 h-3.5" /> Saved</>
            ) : (
              <><Save className="w-3.5 h-3.5" /> Save</>
            )}
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <AlertCircle className="w-3 h-3" />
            {error}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Model used when starting a new session. Live-switch available via StatusBar dropdown.
      </p>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * AgentForm Component
 *
 * @param {object} props
 * @param {Array} props.hosts - List of available hosts
 * @param {object} [props.agent] - Existing agent for edit mode
 * @param {Function} props.onSubmit - Form submission handler
 * @param {Function} props.onCancel - Cancel handler
 */
export default function AgentForm({ hosts = [], agent = null, onSubmit, onCancel }) {
  const isEditMode = !!agent;

  // State
  const [values, setValues] = useState({
    name: agent?.name || '',
    hostId: agent?.hostId || agent?.host?.id || '',
    workingDirectory: agent?.workingDirectory || '',
    displayName: agent?.displayName || '',
    description: agent?.description || '',
    avatarUrl: agent?.avatarUrl || '',
    // SSH Override fields (optional - fallback to host values)
    sshUsername: agent?.sshUsername || '',
    sshKeyPath: agent?.sshKeyPath || '',
    claudePath: agent?.claudePath || ''
  });
  const [showSshOverrides, setShowSshOverrides] = useState(
    // Expand if any SSH override is set
    !!(agent?.sshUsername || agent?.sshKeyPath || agent?.claudePath)
  );
  const [touched, setTouched] = useState({});

  // Track if user has manually edited sshUsername or workingDirectory
  const [sshUsernameManuallyEdited, setSshUsernameManuallyEdited] = useState(false);
  const [workingDirectoryManuallyEdited, setWorkingDirectoryManuallyEdited] = useState(
    // Consider it manually edited if we're editing an existing agent
    isEditMode && !!agent?.workingDirectory
  );

  // Auto-sync sshUsername with agent name in create mode (until user edits it)
  useEffect(() => {
    if (!isEditMode && values.name && !sshUsernameManuallyEdited) {
      setValues(prev => ({ ...prev, sshUsername: prev.name }));
    }
  }, [isEditMode, values.name, sshUsernameManuallyEdited]);

  // Auto-sync workingDirectory with agent name in create mode (until user edits it)
  // Default pattern: /home/{agentname}/projects
  useEffect(() => {
    if (!isEditMode && values.name && !workingDirectoryManuallyEdited) {
      setValues(prev => ({ ...prev, workingDirectory: `/home/${prev.name}/projects` }));
    }
  }, [isEditMode, values.name, workingDirectoryManuallyEdited]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Validation
  const errors = useMemo(() => ({
    name: validateName(values.name),
    hostId: validateHostId(values.hostId),
    workingDirectory: validateWorkingDirectory(values.workingDirectory)
  }), [values]);

  const isValid = !errors.name && !errors.hostId && !errors.workingDirectory;

  // Handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
    setSubmitError(null);
    // Track manual edit of sshUsername to stop auto-sync
    if (name === 'sshUsername') {
      setSshUsernameManuallyEdited(true);
    }
    // Track manual edit of workingDirectory to stop auto-sync
    if (name === 'workingDirectory') {
      setWorkingDirectoryManuallyEdited(true);
    }
  }, []);

  const handleBlur = useCallback((e) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!isEditMode) {
      setTouched({ name: true, hostId: true, workingDirectory: true });
      if (!isValid) return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let data;
      if (isEditMode) {
        // Edit mode: send all editable fields
        data = {
          hostId: values.hostId,
          workingDirectory: values.workingDirectory.trim(),
          displayName: values.displayName?.trim() || null,
          description: values.description?.trim() || null,
          avatarUrl: values.avatarUrl?.trim() || null,
          // SSH Override fields (null to clear/use host defaults)
          sshUsername: values.sshUsername?.trim() || null,
          sshKeyPath: values.sshKeyPath?.trim() || null,
          claudePath: values.claudePath?.trim() || null
        };
      } else {
        // Create mode: send all required fields
        data = {
          name: values.name.trim(),
          hostId: values.hostId,
          workingDirectory: values.workingDirectory.trim(),
          ...(values.displayName?.trim() && { displayName: values.displayName.trim() }),
          ...(values.description?.trim() && { description: values.description.trim() }),
          ...(values.avatarUrl?.trim() && { avatarUrl: values.avatarUrl.trim() }),
          // SSH Override fields (sshUsername defaults to agent name - "Convention over Configuration")
          sshUsername: values.sshUsername?.trim() || values.name.trim(),
          ...(values.sshKeyPath?.trim() && { sshKeyPath: values.sshKeyPath.trim() }),
          ...(values.claudePath?.trim() && { claudePath: values.claudePath.trim() })
        };
      }
      await onSubmit(data);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [values, isValid, isEditMode, onSubmit]);

  // Render
  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-700">
        <div className="p-2 rounded-lg bg-indigo-500/10">
          <Bot className="w-6 h-6 text-indigo-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">
            {isEditMode ? `Edit Agent: ${agent.displayName || agent.name}` : 'Create Agent Slot'}
          </h2>
          <p className="text-sm text-gray-400">
            {isEditMode ? 'Update agent configuration' : 'Reserve a slot for a new agent'}
          </p>
        </div>
      </div>

      {/* Error */}
      {submitError && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-400">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{submitError}</span>
        </div>
      )}

      {/* Fields */}
      <div className="space-y-4">
        {/* Name */}
        <FormField
          id="name"
          label="Name"
          required={!isEditMode}
          icon={Bot}
          error={touched.name ? errors.name : null}
          hint={isEditMode ? "Name cannot be changed" : "Unique identifier (lowercase, alphanumeric with hyphens)"}
        >
          <input
            type="text"
            id="name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="e.g., mochi, dev-agent"
            disabled={isSubmitting || isEditMode}
            className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          />
        </FormField>

        {/* Display Name */}
        <FormField
          id="displayName"
          label="Display Name"
          icon={FileText}
          hint="Human-readable name shown in UI"
        >
          <input
            type="text"
            id="displayName"
            name="displayName"
            value={values.displayName}
            onChange={handleChange}
            placeholder="e.g., Mochi"
            disabled={isSubmitting}
            className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          />
        </FormField>

        {/* Host */}
        <FormField
          id="hostId"
          label="Host"
          required
          icon={Server}
          error={touched.hostId ? errors.hostId : null}
          hint="Host machine where this agent will run"
        >
          <select
            id="hostId"
            name="hostId"
            value={values.hostId}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
            className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            <option value="">Select a host...</option>
            {hosts.map(host => (
              <option key={host.id} value={host.id}>
                {host.name} ({host.hostname})
              </option>
            ))}
          </select>
        </FormField>

        {/* Working Directory */}
        <FormField
          id="workingDirectory"
          label="Working Directory"
          required
          icon={FolderOpen}
          error={touched.workingDirectory ? errors.workingDirectory : null}
          hint="Base directory for agent work on the host"
        >
          <input
            type="text"
            id="workingDirectory"
            name="workingDirectory"
            value={values.workingDirectory}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="/home/user/projects"
            disabled={isSubmitting}
            className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          />
        </FormField>

        {/* Description */}
        <FormField
          id="description"
          label="Description"
          icon={FileText}
          hint="Brief description of the agent's purpose"
        >
          <textarea
            id="description"
            name="description"
            value={values.description}
            onChange={handleChange}
            placeholder="e.g., Handles media server tasks"
            disabled={isSubmitting}
            rows={2}
            className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 resize-none"
          />
        </FormField>

        {/* Avatar */}
        <FormField
          id="avatar"
          label="Avatar"
          icon={Image}
          hint={isEditMode ? "Upload an avatar image" : "Avatar can be uploaded after creating the agent"}
        >
          {isEditMode && agent?.id ? (
            <AvatarUpload
              agentId={agent.id}
              currentUrl={values.avatarUrl}
              agentName={values.displayName || values.name}
              onUpload={(url) => setValues(prev => ({ ...prev, avatarUrl: url }))}
              onDelete={() => setValues(prev => ({ ...prev, avatarUrl: '' }))}
              disabled={isSubmitting}
            />
          ) : (
            <div className="text-sm text-gray-500 py-2">
              Save the agent first to enable avatar upload.
            </div>
          )}
        </FormField>

        {/* SSH Overrides (collapsible) */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowSshOverrides(!showSshOverrides)}
            className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-gray-100 transition-colors"
          >
            {showSshOverrides ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <Key className="w-4 h-4 text-gray-400" />
            SSH Overrides
            <span className="text-xs text-gray-500 font-normal">(optional)</span>
          </button>

          {showSshOverrides && (
            <div className="pl-6 space-y-4 border-l-2 border-gray-700">
              <p className="text-xs text-gray-500">
                Override host defaults for this agent. Leave empty to use host settings.
              </p>

              {/* SSH Username */}
              <FormField
                id="sshUsername"
                label="SSH Username"
                icon={User}
                hint="Linux user for SSH connection"
              >
                <input
                  type="text"
                  id="sshUsername"
                  name="sshUsername"
                  value={values.sshUsername}
                  onChange={handleChange}
                  placeholder={agent?.host?.username || 'Uses host default'}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                />
              </FormField>

              {/* SSH Key Path */}
              <FormField
                id="sshKeyPath"
                label="SSH Key Path"
                icon={Key}
                hint="Path to SSH private key on backend server"
              >
                <input
                  type="text"
                  id="sshKeyPath"
                  name="sshKeyPath"
                  value={values.sshKeyPath}
                  onChange={handleChange}
                  placeholder={agent?.host?.sshKeyPath || 'Uses host default'}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                />
              </FormField>

              {/* Claude Path */}
              <FormField
                id="claudePath"
                label="Claude CLI Path"
                icon={Terminal}
                hint="Path to Claude CLI binary on remote host"
              >
                <input
                  type="text"
                  id="claudePath"
                  name="claudePath"
                  value={values.claudePath}
                  onChange={handleChange}
                  placeholder={agent?.host?.claudePath || 'Uses host default (claude)'}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                />
              </FormField>
            </div>
          )}
        </div>

        {/* Input History - Only show in edit mode */}
        {isEditMode && agent?.id && (
          <InputHistorySection agentId={agent.id} />
        )}

        {/* Self-Note Settings - Only show in edit mode */}
        {isEditMode && agent?.id && (
          <SelfNoteSettingsSection agent={agent} />
        )}

        {/* System Prompt - Only show in edit mode */}
        {isEditMode && agent?.id && (
          <SystemPromptSection agent={agent} />
        )}

        {/* Whisper Mode - Only show in edit mode */}
        {isEditMode && agent?.id && (
          <WhisperModeSection agent={agent} />
        )}

        {/* Default Model - Only show in edit mode */}
        {isEditMode && agent?.id && (
          <ModelSection agent={agent} />
        )}

        {/* CLAUDE.md Edit Trust Mode - Only show in edit mode */}
        {isEditMode && agent?.id && (
          <EditTrustModeSection agent={agent} />
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || (!isEditMode && !isValid)}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isEditMode ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isEditMode ? 'Save Changes' : 'Create Slot'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
